import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import type {
  ContactFormData,
  FormErrors,
  ProjectTypeOption,
  BudgetOption,
  ContactMethodOption,
} from '../types';

interface ContactFormProps {
  projectTypes: ProjectTypeOption[];
  budgetOptions: BudgetOption[];
  contactMethods: ContactMethodOption[];
}

const ContactForm = ({ projectTypes, budgetOptions, contactMethods }: ContactFormProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<ContactFormData>({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    budget: '',
    preferredContact: '',
    message: '',
    newsletter: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const validateField = (name: keyof ContactFormData, value: string): string | undefined => {
    switch (name) {
      case 'fullName':
        if (!value.trim()) return t('contact.validation.fullNameRequired', 'Full name is required');
        if (value.trim().length < 2) return t('contact.validation.fullNameMin', 'Name must be at least 2 characters');
        break;
      case 'email':
        if (!value.trim()) return t('contact.validation.emailRequired', 'Email is required');
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return t('contact.validation.emailInvalid', 'Invalid email format');
        break;
      case 'phone':
        if (!value.trim()) return t('contact.validation.phoneRequired', 'Phone number is required');
        if (!/^\+?[\d\s-()]{10,}$/.test(value)) return t('contact.validation.phoneInvalid', 'Invalid phone number');
        break;
      case 'company':
        if (!value.trim()) return t('contact.validation.companyRequired', 'Company name is required');
        break;
      case 'projectType':
        if (!value) return t('contact.validation.projectTypeRequired', 'Please select a project type');
        break;
      case 'budget':
        if (!value) return t('contact.validation.budgetRequired', 'Please select a budget range');
        break;
      case 'preferredContact':
        if (!value) return t('contact.validation.contactMethodRequired', 'Please select a contact method');
        break;
      case 'message':
        if (!value.trim()) return t('contact.validation.messageRequired', 'Message is required');
        if (value.trim().length < 20) return t('contact.validation.messageMin', 'Message must be at least 20 characters');
        break;
    }
    return undefined;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    const error = validateField(name as keyof ContactFormData, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSelectChange = (name: keyof ContactFormData) => (value: string | number) => {
    setFormData((prev) => ({ ...prev, [name]: value as string }));
    const error = validateField(name, value as string);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, newsletter: e.target.checked }));
  };

  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    if (step === 1) {
      const fields: (keyof ContactFormData)[] = ['fullName', 'email', 'phone', 'company'];
      fields.forEach((field) => {
        const error = validateField(field, formData[field] as string);
        if (error) {
          newErrors[field] = error;
          isValid = false;
        }
      });
    } else if (step === 2) {
      const fields: (keyof ContactFormData)[] = ['projectType', 'budget', 'preferredContact'];
      fields.forEach((field) => {
        const error = validateField(field, formData[field] as string);
        if (error) {
          newErrors[field] = error;
          isValid = false;
        }
      });
    } else if (step === 3) {
      const error = validateField('message', formData.message);
      if (error) {
        newErrors.message = error;
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 3));
    }
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateStep(3)) {
      return;
    }

    setIsSubmitting(true);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080';
      const response = await fetch(`${apiUrl}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const result = await response.json();
      console.log('Form submitted:', result);

      setSubmitSuccess(true);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        company: '',
        projectType: '',
        budget: '',
        preferredContact: '',
        message: '',
        newsletter: false,
      });
      setCurrentStep(1);

      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors({ message: 'Failed to submit form. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-3xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex items-center flex-1">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all duration-300 ${
                  currentStep >= step
                  ? 'bg-gradient-cta text-white' : 'bg-surface text-secondary border border-border'
                }`}
              >
                {step}
              </div>
              {step < 3 && (
                <div
                  className={`flex-1 h-1 mx-2 transition-all duration-300 ${
                    currentStep > step ? 'bg-accent' : 'bg-border'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="text-center">
          <p className="text-sm text-secondary">
            {t('contact.labels.step')} {currentStep} {t('contact.labels.of')} 3:{' '}
            {currentStep === 1 && t('contact.labels.personalInfo')}
            {currentStep === 2 && t('contact.labels.projectDetails')}
            {currentStep === 3 && t('contact.labels.yourMessage')}
          </p>
        </div>
      </div>

      {submitSuccess && (
        <div className="mb-6 p-4 rounded-lg bg-success/10 border border-success/30 flex items-start gap-3">
          <Icon name="CheckCircle2" size={24} color="var(--color-success)" />
          <div>
            <h4 className="font-semibold text-success mb-1">{t('contact.labels.successTitle')}</h4>
            <p className="text-sm text-secondary">
              {t('contact.labels.successDesc')}
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {currentStep === 1 && (
          <div className="space-y-4">
            <Input
              className='bg-blue-200'
              label={t('contact.labels.fullName')}
              type="text"
              name="fullName"
              placeholder={t('contact.placeholders.fullName')}
              value={formData.fullName}
              onChange={handleInputChange}
              error={errors.fullName}
              required
            />
            <Input
              className='bg-blue-200'
              label={t('contact.labels.email')}
              type="email"
              name="email"
              placeholder={t('contact.placeholders.email')}
              value={formData.email}
              onChange={handleInputChange}
              error={errors.email}
              required
              description={t('contact.labels.emailDescription', "We'll never share your email with anyone else")}
            />
            <Input
              className='bg-blue-200'
              label={t('contact.labels.phone')}
              type="tel"
              name="phone"
              placeholder={t('contact.placeholders.phone')}
              value={formData.phone}
              onChange={handleInputChange}
              error={errors.phone}
              required
            />
            <Input
              className='bg-blue-200'
              label={t('contact.labels.company')}
              type="text"
              name="company"
              placeholder={t('contact.labels.companyPlaceholder')}
              value={formData.company}
              onChange={handleInputChange}
              error={errors.company}
              required
            />
          </div>
        )}

        {currentStep === 2 && (
          <div className="space-y-4">
            <Select
              label={t('contact.labels.projectType')}
              options={projectTypes}
              value={formData.projectType}
              onChange={handleSelectChange('projectType')}
              error={errors.projectType}
              required
              placeholder={t('contact.labels.selectProjectType')}
            />
            <Select
              label={t('contact.labels.budget')}
              options={budgetOptions}
              value={formData.budget}
              onChange={handleSelectChange('budget')}
              error={errors.budget}
              required
              placeholder={t('contact.labels.selectBudgetRange')}
            />
            <Select
              label={t('contact.labels.contactMethod')}
              options={contactMethods}
              value={formData.preferredContact}
              onChange={handleSelectChange('preferredContact')}
              error={errors.preferredContact}
              required
              placeholder={t('contact.labels.selectContactMethod')}
            />
          </div>
        )}

        {currentStep === 3 && (
          <div className="space-y-4">
            <div>
              <label
                htmlFor="message"
                className="block text-sm font-medium text-primary mb-2"
              >
                {t('contact.labels.message')} <span className="text-error">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                rows={6}
                placeholder={t('contact.placeholders.message')}
                value={formData.message}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 rounded-lg bg-surface border ${
                  errors.message ? 'border-error' : 'border-border'
                  } text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-300 resize-none`}
                required
              />
              {errors.message && <p className="mt-1 text-sm text-error">{errors.message}</p>}
              <p className="mt-1 text-xs text-secondary">{t('contact.labels.minChars')}</p>
            </div>
            <Checkbox
              label={t('contact.labels.newsletter')}
              checked={formData.newsletter}
              onChange={handleCheckboxChange}
              description={t('contact.labels.newsletterDesc', "Get monthly updates on web development trends and best practices")}
            />
          </div>
        )}

        <div className="flex gap-4 pt-4">
          {currentStep > 1 && (
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={handlePrevStep}
              iconName="ChevronLeft"
              iconPosition="left"
              className="flex-1"
            >
              {t('contact.labels.previous')}
            </Button>
          )}
          {currentStep < 3 ? (
            <Button
              type="button"
              variant="default"
              size="lg"
              onClick={handleNextStep}
              iconName="ChevronRight"
              iconPosition="right"
              className="flex-1 gradient-cta"
            >
              {t('contact.labels.nextStep')}
            </Button>
          ) : (
            <Button
              type="submit"
              variant="default"
              size="lg"
              loading={isSubmitting}
              disabled={isSubmitting}
              iconName="Send"
              iconPosition="right"
              className="flex-1 gradient-cta"
            >
              {isSubmitting ? t('contact.labels.sending') : t('contact.labels.sendMessage')}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ContactForm;